import * as Sentry from "@sentry/react";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import axios from "axios";
import ReactDOM from "react-dom/client";

import config from "./config";
import "./index.css";
// Import the generated route tree
import { routeTree } from "./routeTree.gen";

// Create a new router instance
const router = createRouter({ routeTree });

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// Check if on a public route (no authenticated user expected)
const isPublicRoute = () => {
  const path = window.location.pathname;
  return path.startsWith("/signin") || path.startsWith("/workspaces/");
};

if (config.APP_ENV === "production") {
  Sentry.init({
    // Only capture real application errors, not network/API issues
    beforeSend(event, hint) {
      const error = hint?.originalException;
      const message = event.exception?.values?.[0]?.value || "";
      const frames = event.exception?.values?.[0]?.stacktrace?.frames;

      // Skip Axios errors - API errors are tracked server-side
      if (axios.isAxiosError(error)) return null;

      // Skip network errors - client connectivity issues
      if (
        message.includes("Network Error") ||
        message.includes("Failed to fetch") ||
        message.includes("Load failed")
      )
        return null;

      // Skip Google Maps internal errors
      if (frames?.some((f) => f.filename?.includes("maps.googleapis.com")))
        return null;

      // Skip browser extension errors
      if (
        frames?.some(
          (f) =>
            f.filename?.includes("chrome-extension://") ||
            f.filename?.includes("moz-extension://"),
        )
      )
        return null;

      return event;
    },
    dsn: "https://6103bf9235652295894b93ce6037cc2a@o4504831428919296.ingest.us.sentry.io/4508464387653632",
    environment: config.APP_ENV,
    integrations: [
      Sentry.replayIntegration({
        // Don't record replays for internal users
        beforeAddRecordingEvent: (event) => {
          const user = Sentry.getCurrentScope().getUser();
          // Skip if no user or internal user
          if (!user?.email || user.email.endsWith("@fulflld.com")) return null;
          return event;
        },
      }),
      Sentry.feedbackIntegration({ autoInject: false }),
    ],
    // Breadcrumbs help reproduce what user did before error
    maxBreadcrumbs: 50,
    release:
      process.env.SENTRY_RELEASE_ID ||
      `fulflld-bunker@${new Date().toISOString().split("T")[0]}`,
    // Only record replays on authenticated routes, not public pages
    replaysOnErrorSampleRate: isPublicRoute() ? 0 : 1.0,
    replaysSessionSampleRate: 0, // Don't record random sessions, only on error
    // Don't send personal info
    sendDefaultPii: false,
  });
}

// Apply scrollbar fix for ChromeOS — overlay scrollbars are invisible by default
if (/CrOS/.test(navigator.userAgent)) {
  document.documentElement.classList.add("scrollbar-fix");
}

// Render the app
const rootElement = document.getElementById("root")!;
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);

  root.render(
    // Need to remove strictmode for now due to google maps marker issues
    // <StrictMode>
    <RouterProvider router={router} />,
    // </StrictMode>
  );
}
